// ─────────────────────────────────────────────────────────────────────────────
// persistence.ts — IndexedDB-backed notebook persistence for PWA mode
//
// Replaces the Node.js file_format.ts used by the VS Code extension host.
// All notebook data lives in IndexedDB, which is fast, has no practical size
// limit, works offline, and is available on every modern browser + Android.
//
// DB structure (one database per notebook):
//   Object stores:
//     meta        — single record: notebook metadata + page list + sections
//     actions     — append-only action records (auto-increment key)
//     strokes     — per-page stroke maps (key = pageId)
//     settings    — single record: user preferences
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction } from '@shared/grimoire_action';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SerialPageEntry {
  id: string;
  index: number;
  title: string;
  color?: string;
  sectionId?: string;
  isSectionHeader?: boolean;
  densityLevel: number;
  /** Per-page decoration slots — registered style ids.
   *    body:    { justification, graphics }
   *    margins: { utility, graphics }
   *    frame:   single ornament id */
  decorations?: {
    body?: { justification?: string; graphics?: string };
    margins?: { utility?: string; graphics?: string };
    frame?: string;
  };
  bookmark?: {
    id: string;
    label: string;
    tier: 'major' | 'minor';
    parentId?: string;
    color?: string;
  };
  blockCount: number;
  arrowCount: number;
  updatedAt: number;
  blocks?: unknown[];
  arrows?: unknown[];
}

export interface SerialSectionEntry {
  id: string;
  title: string;
  description: string;
  color: string;
  pageIds: string[];
  headerPageId: string;
  kind?: string;
  createdAt: number;
}

export interface NotebookIndex {
  schema: '1.1';
  meta: {
    id: string;
    title: string;
    createdAt: number;
    updatedAt: number;
    coverSeed: number;
  };
  pages: SerialPageEntry[];
  sections?: SerialSectionEntry[];
  currentPageId?: string;
  savedAt: number;
}

export interface StrokeData {
  id: string;
  points: Array<{
    x: number; y: number; pressure: number;
    tiltX: number; tiltY: number; twist: number;
    timestamp: number;
  }>;
  style: Record<string, unknown>;
  timestamp: number;
  blockId?: string;
  /** Page composition layer (0–5). Retired but kept for backward
   *  compatibility with older saves. */
  pageLayer?: number;
  // ── Content modifiers (per-stroke) ──
  locked?: boolean;
  groupId?: string;
  astral?: { birth: number; ttlMs: number };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DB_NAME = 'grimoire-notebook';
const DB_VERSION = 1;

const STORE_META = 'meta';
const STORE_ACTIONS = 'actions';
const STORE_STROKES = 'strokes';
const STORE_SETTINGS = 'settings';

const META_KEY = 'notebook-index';
const SETTINGS_KEY = 'user-settings';

// ── Database ──────────────────────────────────────────────────────────────────

let _db: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
      if (!db.objectStoreNames.contains(STORE_ACTIONS)) {
        db.createObjectStore(STORE_ACTIONS, { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_STROKES)) {
        db.createObjectStore(STORE_STROKES);
      }
      if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
        db.createObjectStore(STORE_SETTINGS);
      }
    };

    req.onsuccess = () => {
      _db = req.result;
      resolve(_db);
    };

    req.onerror = () => reject(req.error);
  });
}

// ── Generic helpers ───────────────────────────────────────────────────────────

function idbPut(store: string, key: IDBValidKey, value: unknown): Promise<void> {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  }));
}

function idbAdd(store: string, value: unknown): Promise<void> {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).add(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function idbGetAll<T>(store: string): Promise<T[]> {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  }));
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Save the notebook index (metadata + page list + sections). */
export async function saveIndex(index: NotebookIndex): Promise<void> {
  index.savedAt = Date.now();
  index.meta.updatedAt = Date.now();
  await idbPut(STORE_META, META_KEY, index);
}

/** Load the notebook index. Returns null if no notebook exists yet. */
export async function loadIndex(): Promise<NotebookIndex | null> {
  const data = await idbGet<NotebookIndex>(STORE_META, META_KEY);
  return data ?? null;
}

/** Append a single action to the action log. */
export async function appendAction(action: GrimoireAction): Promise<void> {
  await idbAdd(STORE_ACTIONS, action);
}

/** Load all actions from the action log (for replay on startup). */
export async function loadAllActions(): Promise<GrimoireAction[]> {
  return idbGetAll<GrimoireAction>(STORE_ACTIONS);
}

/** Save strokes for a specific page. */
export async function savePageStrokes(pageId: string, strokes: StrokeData[]): Promise<void> {
  await idbPut(STORE_STROKES, pageId, strokes);
}

/** Load strokes for a specific page. Returns empty array if none exist. */
export async function loadPageStrokes(pageId: string): Promise<StrokeData[]> {
  const data = await idbGet<StrokeData[]>(STORE_STROKES, pageId);
  return data ?? [];
}

/** Save all page strokes at once (bulk persist). */
export async function saveAllStrokes(pageStrokes: Map<string, Map<string, StrokeData>>): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_STROKES, 'readwrite');
    const store = tx.objectStore(STORE_STROKES);
    for (const [pageId, strokeMap] of pageStrokes) {
      store.put([...strokeMap.values()], pageId);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Save user settings. */
export async function saveSettings(settings: Record<string, unknown>): Promise<void> {
  await idbPut(STORE_SETTINGS, SETTINGS_KEY, settings);
}

/** Load user settings. Returns null if none saved. */
export async function loadSettings(): Promise<Record<string, unknown> | null> {
  const data = await idbGet<Record<string, unknown>>(STORE_SETTINGS, SETTINGS_KEY);
  return data ?? null;
}

/** Check if a notebook exists in IndexedDB. */
export async function hasNotebook(): Promise<boolean> {
  const index = await loadIndex();
  return index !== null;
}

/** Clear all notebook data (for testing / reset). */
export async function clearAll(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      [STORE_META, STORE_ACTIONS, STORE_STROKES, STORE_SETTINGS],
      'readwrite',
    );
    tx.objectStore(STORE_META).clear();
    tx.objectStore(STORE_ACTIONS).clear();
    tx.objectStore(STORE_STROKES).clear();
    tx.objectStore(STORE_SETTINGS).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
